import React from "react";
import "../styles/modal.css";
import { GlobalStyles } from "@mui/material";
import {
  Box,
  Fade,
  Modal as MuiModal,
  Grid,
  Button,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { connect, ConnectedProps } from "react-redux";
import {
  hideChildModal,
  hideModal,
  ModalProperties,
  ModalSize,
} from "../data/modal";

import ContainerForm from "../components/Forms/Container";
import ContainerTypeForm from "../components/Forms/ContainerType";
import LabelForm from "../components/Forms/Label";
import TenantForm from "../components/Forms/Tenant";
import TerminalForm from "../components/Forms/Terminal";
import TerminalCallForm from "../components/Forms/TerminalCall";
import TerminalCallDetails from "../components/ModalView/TerminalCallDetails";
import VesselForm from "../components/Forms/Vessel";
import ContactForm from "../components/Forms/Contact";
import ContainerModal from "../components/ModalView/Container";
import TerminalMapModal from "../components/ModalView/TerminalMap";
import TerminalListDialog from "../components/ModalView/TerminalList";
import TerminalCallNoteForm from "./Forms/TerminalCallNote";
import ContainerTerminalCallModal from "./ModalView/ContainerTerminalCall";
import FileView from "./partial/FileView";
import VesselGridSettings from "./Forms/VesselGridSettings";
import ContainerUploadForm from "./Forms/ContainerUpload";
import ContainerTerminalCallForm from "./Forms/ContainerTerminalCall";
import TerminalCallCompletionForm from "./Forms/TerminalCallCompletion";
import TerminalCallMailerForm from "./Forms/TerminalCallMailer";
import CompletedTerminalCallList from "./ModalView/CompletedTerminalCallList";
import VoyageForm from "./Forms/Voyage";
import SimpleTerminalCallForm from "./Forms/SimpleTerminalCall";
import SimpleMultipleContainerForm from "./Forms/SimpleMultipleContainer";
import TerminalCallDatetimeForm from "./Forms/TerminalCallDatetime";
import TerminalCallDetailsDashboard from "./ModalView/TerminalCallDetailsDashboard";

import GasoilBunkeringModal from "./ModalView/GasoilBunkeringModal";
import GasoilDialogForm from "./vessel/gasoil/GasoilDialogForm";
import BunkeringDialogForm from "./vessel/gasoil/BunkeringDialogForm";

import GenesisUserForm from "./ModalForm/GenesisUser";
import GenesisTenantForm from "./ModalForm/GenesisTenant";
import GenesisTerminalForm from "./ModalForm/GenesisTerminal";

const EMPTY_MODAL_STACK: ModalProperties[] = [];

const mapStateToProps = (state: any) => ({
  modalStack: state.modal.modalStack ?? EMPTY_MODAL_STACK,
});

const mapDispatchToProps = {
  dispatchHideModal: hideModal,
  dispatchHideChildModal: hideChildModal,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type ModalProps = {} & ConnectedProps<typeof connector>;

type ModalSizing = {
  width: string;
  maxWidth: string | number;
};

const getModalSizing = (size?: ModalSize | string): ModalSizing => {
  switch (size) {
    case "compact":
      return {
        width: "40vw",
        maxWidth: 700,
      };

    case "large":
      return {
        width: "92vw",
        maxWidth: 1500,
      };

    case "wide":
      return {
        width: "98vw",
        maxWidth: "98vw",
      };

    case "default":
    default:
      return {
        width: "90vw",
        maxWidth: 1200,
      };
  }
};

function Modal(props: ModalProps) {
  const { dispatchHideModal, dispatchHideChildModal, modalStack } = props;

  if (!modalStack || modalStack.length === 0) {
    return null;
  }

  const renderModalContent = (
    form?: string,
    formProps?: any,
    injectedProps?: any
  ) => {
    const finalProps = {
      ...(formProps ?? {}),
      ...(injectedProps ?? {}),
    };

    switch (form) {
      case "container":
        return <ContainerForm {...finalProps} />;

      case "containerType":
        return <ContainerTypeForm {...finalProps} />;

      case "label":
        return <LabelForm {...finalProps} />;

      case "tenant":
        return <TenantForm {...finalProps} />;

      case "terminal":
        return <TerminalForm {...finalProps} />;

      case "terminallist":
        return <TerminalListDialog {...finalProps} />;

      case "terminalCall":
        return <TerminalCallForm {...finalProps} />;

      case "terminalCallDetails":
        return <TerminalCallDetails {...finalProps} />;

      case "vessel":
        return <VesselForm {...finalProps} />;

      case "contact":
        return <ContactForm {...finalProps} />;

      case "terminalmapmodal":
        return <TerminalMapModal {...finalProps} />;

      case "containerviewmodal":
        return <ContainerModal {...finalProps} />;

      case "note":
        return <TerminalCallNoteForm {...finalProps} />;

      case "containerterminalcall":
        return <ContainerTerminalCallModal {...finalProps} />;

      case "fileview":
        return <FileView {...finalProps} />;

      case "vesselgridsettings":
        return <VesselGridSettings {...finalProps} />;

      case "containerupload":
        return <ContainerUploadForm {...finalProps} />;

      case "containerterminalcallform":
        return <ContainerTerminalCallForm {...finalProps} />;

      case "terminalcallcompletion":
        return <TerminalCallCompletionForm {...finalProps} />;

      case "terminalcallmailer":
        return <TerminalCallMailerForm {...finalProps} />;

      case "completedterminalcalllist":
        return <CompletedTerminalCallList {...finalProps} />;

      case "voyageform":
        return <VoyageForm {...finalProps} />;

      case "simpleterminalcall":
        return <SimpleTerminalCallForm {...finalProps} />;

      case "simplemultiplecontainer":
        return <SimpleMultipleContainerForm {...finalProps} />;

      case "terminalcalldatetime":
        return <TerminalCallDatetimeForm {...finalProps} />;

      case "plannerterminalcalldetails":
        return <TerminalCallDetailsDashboard {...finalProps} />;

      case "vesselgasoil":
        return <GasoilBunkeringModal {...finalProps} />;

      case "vesselgasoilform":
        return <GasoilDialogForm {...finalProps} />;

      case "vesselbunkeringform":
        return <BunkeringDialogForm {...finalProps} />;
        
      case "genesis-users":
        return <GenesisUserForm {...finalProps} />;

      case "genesis-tenants":
        return <GenesisTenantForm {...finalProps} />;

      case "genesis-terminals":
        return <GenesisTerminalForm {...finalProps} />;
      default:
        return null;
    }
  };

  const renderWrapper = (
    modalData: ModalProperties,
    onClose: () => void,
    index: number,
    isTopModal: boolean
  ) => {
    const hideCloseButton =
      Boolean((modalData as any).hideCloseButton) ||
      Boolean(modalData.formProps?.hideCloseButton);

    const injectedProps = {
      onClose,
      isChildModal: index > 0,
      modalIndex: index,
      isTopModal,
    };

    return (
      <>
        <Grid
          item
          className="modal-header"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #ccc",
            flex: "0 0 auto",
          }}
        >
          <Grid item>
            <Typography variant="h6" fontWeight={700}>
              {modalData.title ?? ""}
            </Typography>

            {modalData.subtitle ? (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {modalData.subtitle}
              </Typography>
            ) : null}
          </Grid>

          {!hideCloseButton ? (
            <Grid item>
              <Button type="button" onClick={onClose}>
                <CloseIcon fontSize="inherit" />
              </Button>
            </Grid>
          ) : null}
        </Grid>

        <Grid
          item
          className="modal-content"
          sx={{
            px: 2,
            py: 2,
            overflowY: "auto",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          {renderModalContent(
            modalData.form,
            modalData.formProps,
            injectedProps
          )}
        </Grid>
      </>
    );
  };

  const renderModalBox = (
    modalData: ModalProperties,
    index: number,
    isTopModal: boolean
  ) => {
    const sizing = getModalSizing(
      modalData.size ?? modalData.formProps?.size ?? "default"
    );

    const offset = index * 18;
    const zIndex = 1300 + index * 20;

    const closeAction =
      index === 0 && modalStack.length === 1
        ? dispatchHideModal
        : dispatchHideChildModal;

    const disableBackdropClose =
      Boolean((modalData as any).disableBackdropClose) ||
      Boolean(modalData.formProps?.disableBackdropClose);

    const onClose = (_event?: any, reason?: string) => {
      if (
        disableBackdropClose &&
        (reason === "backdropClick" || reason === "escapeKeyDown")
      ) {
        return;
      }

      closeAction();
    };

    const modalKey =
      modalData.formProps?.modalKey ??
      modalData.formProps?.["@id"] ??
      modalData.formProps?.id ??
      `${modalData.form ?? "modal"}-${index}`;

    return (
      <MuiModal
        key={modalKey}
        open
        hideBackdrop={index > 0}
        keepMounted={false}
        disableEnforceFocus={!isTopModal}
        disableAutoFocus={!isTopModal}
        disableRestoreFocus={!isTopModal}
        onClose={onClose}
        aria-labelledby={`modal-title-${index}`}
        aria-describedby={`modal-description-${index}`}
        sx={{
          zIndex,
          pointerEvents: isTopModal ? "auto" : "none",
        }}
      >
        <Fade in timeout={180}>
          <Box>
            <Box
              sx={{
                position: "fixed",
                top: `calc(50% + ${offset}px)`,
                left: `calc(50% + ${offset}px)`,
                transform: "translate(-50%, -50%)",
                width: sizing.width,
                maxWidth: sizing.maxWidth,
                maxHeight: "95vh",
                bgcolor: "background.paper",
                borderRadius: 2,
                boxShadow: 24,
                outline: "none",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                zIndex,
                pointerEvents: "auto",
              }}
            >
              {renderWrapper(
                modalData,
                () => closeAction(),
                index,
                isTopModal
              )}
            </Box>
          </Box>
        </Fade>
      </MuiModal>
    );
  };

  return (
    <>
      <GlobalStyles
        styles={{
          ".modal-content": {
            flexGrow: 1,
            overflowY: "auto",
            padding: 16,
            paddingBottom: "5px !important",
            backgroundColor: "#fff",
          },
          ".button-wrapper": {
            position: "sticky",
            bottom: 0,
            backgroundColor: "#fff",
            padding: "16px",
            borderTop: "1px solid #ccc",
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            zIndex: 10,
          },
        }}
      />

      {modalStack.map((modalData: ModalProperties, index: number) =>
        renderModalBox(modalData, index, index === modalStack.length - 1)
      )}
    </>
  );
}

export default connector(Modal);